import { useCallback } from 'react';
import { buildCreatePlayDto } from '../core/dtos/index';
import { validatePlay, isWindowOpen, getActiveWindow } from '../core/rules/validation';
import {
  applyConstraintCaps,
  parseNumberConstraint,
} from '../core/rules/numberConstraints';
import { getCurrentTurn } from '../core/rules/playTurns';
import { normalizeError } from '../core/errors/index';
import { USER_MESSAGE_KEYS } from '../core/errors/errorCodes';
import { createGuaranteeTicket } from '../core/models/GuaranteeTicket';
import type { PlayLike } from '../core/rules/numberCaps';
import { getServerNow } from '../services/time/serverClock';
import type { HouseConfig } from '../types';
import type { IDataAdapter } from '../adapters/data/IDataAdapter';
import { useDataSync } from './useDataSync';

export type SubmitPlayFeedback = 'accepted' | 'queued' | null;

interface SubmitPlayArgs {
  adapter: Pick<IDataAdapter, 'addPlay'> & Partial<Pick<IDataAdapter, 'subscribe'>> & {
    activeHouseId?: string;
    houseId?: string;
    name?: string;
  };
  online: boolean;
  enqueue: (item: { clientId: string; type: string; payload: unknown }) => void;
  trackPending: (ticket: ReturnType<typeof createGuaranteeTicket>) => void;
  trackAccepted: (
    ticket: ReturnType<typeof createGuaranteeTicket>,
    playId: string,
    claimCode?: string,
  ) => void;
  trackFailed?: (ticket: ReturnType<typeof createGuaranteeTicket>, rejectKey: string) => void;
  config: HouseConfig;
  housePlays: PlayLike[];
  activeHouseId?: string;
  user?: { id?: string; name?: string; houseId?: string };
}

export function useSubmitPlay({
  adapter,
  online,
  enqueue,
  trackPending,
  trackAccepted,
  trackFailed,
  config,
  housePlays,
  activeHouseId,
  user,
}: SubmitPlayArgs) {
  const rawConstraints = useDataSync(
    adapter.subscribe ? adapter as IDataAdapter : null,
    'numberConstraints',
    [] as Array<Record<string, unknown>>,
  );
  const submit = useCallback(async (
    fields: { type: string; num: string; num2: string; stake: string; buyerNote?: string },
    onResult: (result: { feedback: SubmitPlayFeedback; errKey: string | null }) => void,
  ) => {
    const now = getServerNow();
    const window = getActiveWindow(config, now) ?? 'day';
    const clientId = crypto.randomUUID();
    const dto = buildCreatePlayDto({
      type: fields.type,
      number: fields.num,
      number2: fields.type === 'parle' ? fields.num2 : undefined,
      stake: Number(fields.stake),
      houseId: activeHouseId || user?.houseId,
      listerId: user?.id,
      listerName: user?.name,
      window,
      clientId,
      buyerNote: fields.buyerNote,
    });
    const constraints = rawConstraints
      .map((row) => parseNumberConstraint(row, String(row.id ?? '')))
      .filter((row): row is NonNullable<typeof row> => Boolean(row));
    const turn = getCurrentTurn(config);
    const capConfig = turn
      ? applyConstraintCaps({
        perNumberCap: config.perNumberCap,
        numberCaps: config.numberCaps,
        surgePlayThreshold: config.surgePlayThreshold,
        surgeCapRatio: config.surgeCapRatio,
      }, constraints, turn, dto.type)
      : config;
    const err = validatePlay(dto, {
      perNumberCap: capConfig.perNumberCap,
      numberCaps: capConfig.numberCaps,
      surgePlayThreshold: capConfig.surgePlayThreshold,
      surgeCapRatio: capConfig.surgeCapRatio,
      isWindowOpen: isWindowOpen(config, now),
      existingPlays: housePlays as PlayLike[],
      window,
    });
    if (err) {
      onResult({ feedback: null, errKey: err });
      return;
    }

    const ticket = createGuaranteeTicket({
      clientId,
      type: fields.type,
      number: fields.num,
      number2: fields.type === 'parle' ? fields.num2 : undefined,
      stake: Number(fields.stake),
      createdAt: now.getTime(),
      buyerNote: dto.buyerNote,
    });

    if (!online) {
      enqueue({ clientId, type: 'addPlay', payload: dto });
      trackPending(ticket);
      // Queued is not confirmed — Aval only after server acceptance.
      onResult({ feedback: 'queued', errKey: null });
      return;
    }

    try {
      const play = await adapter.addPlay(dto) as { id?: string; claimCode?: string };
      trackAccepted(ticket, String(play.id ?? ''), play.claimCode ? String(play.claimCode) : undefined);
      onResult({ feedback: 'accepted', errKey: null });
    } catch (raw) {
      const appErr = normalizeError(raw);
      if (appErr.shouldQueueOffline()) {
        enqueue({ clientId, type: 'addPlay', payload: dto });
        trackPending(ticket);
        onResult({ feedback: 'queued', errKey: null });
        return;
      }
      const errKey = appErr.isValidation()
        ? appErr.userMessageKey
        : appErr.userMessageKey ?? USER_MESSAGE_KEYS.PLAY_FAILED;
      trackFailed?.(ticket, errKey);
      onResult({ feedback: null, errKey });
    }
  }, [
    adapter, online, enqueue, trackPending, trackAccepted, trackFailed,
    config, housePlays, activeHouseId, user, rawConstraints,
  ]);

  return { submit };
}
